/**
 * UI data hooks.
 *
 * These hooks ONLY consume `application/index.js`. They never touch ports
 * or adapters. That layering is what keeps the architecture protected.
 */
import { useEffect, useState, useCallback } from "react";
import { app } from "../application";

export function usePortfolioSize() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setData(await app.home.countActors());
      setLoading(false);
    })();
  }, []);
  return { data, loading };
}

export function useEngines() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    setData(await app.home.listEngineSummaries());
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

export function useEngineMetrics(engineId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    if (!engineId) return;
    setLoading(true);
    setData(await app.engine(engineId).getMetrics());
    setLoading(false);
  }, [engineId]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

export function useOpportunities(filters = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(filters);
  const load = useCallback(async () => {
    setLoading(true);
    // engine-scoped listing when possible, cross-engine otherwise
    const list = filters.engineId
      ? await app.engine(filters.engineId).listOpportunities(filters)
      : await app.wallet.listOpportunities(filters);
    setData(list);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load, setData };
}

export function useOpportunity(id) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    if (!id) { setData(null); setLoading(false); return; }
    setLoading(true);
    setData(await app.opportunity.get(id));
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load, setData };
}

export function useClosures(limit = 100) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    setData(await app.closures.list({ limit }));
    setLoading(false);
  }, [limit]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

export function useTeam() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setData(await app.team.list());
      setLoading(false);
    })();
  }, []);
  return { data, loading };
}

export function useForecast() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setData(await app.forecast.get());
      setLoading(false);
    })();
  }, []);
  return { data, loading };
}

export function useIntegrations() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setData(await app.integrations.list());
      setLoading(false);
    })();
  }, []);
  return { data, loading };
}
